import React, { useCallback } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { usePaymentMethodsList } from '@/features/payments/hooks/usePaymentMethodsList';
import { usePaymentMethodActions } from '@/features/payments/hooks/usePaymentMethodActions';
import { PaymentMethodsSection } from '@/components/residents/redesign/payments';
import type { PaymentMethod } from '@/components/residents/redesign/payments';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';

/**
 * Desktop Payment Methods View (Account Settings)
 * Uses the unified redesign PaymentMethodsSection with interactive grid.
 */
export const PaymentMethodsView: React.FC = () => {
  const navigation = useNavigation<any>();
  const { methods, refetch } = usePaymentMethodsList();

  const {
    setAsDefault,
    deleteMethodWithConfirmation,
    showDeleteConfirm,
    confirmDelete,
    cancelDelete,
    isUpdating,
  } = usePaymentMethodActions(refetch);

  const handleSetAsDefault = useCallback((method: PaymentMethod) => {
    setAsDefault(method);
  }, [setAsDefault]);

  const handleDelete = useCallback((method: PaymentMethod) => {
    deleteMethodWithConfirmation({
      ...method,
      name: method.label,
      last4: method.lastFour,
    });
  }, [deleteMethodWithConfirmation]);

  const handleAddNew = useCallback(() => {
    navigation.navigate('AddOrChangePaymentMethod', { paymentType: 'card', managePaymentPage: 'ManageMyWallet' });
  }, [navigation]);

  const handleManage = useCallback(() => {
    navigation.navigate('ManageMyWallet');
  }, [navigation]);

  return (
    <>
      <ScrollView style={styles.root} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false}>
        <PaymentMethodsSection
          methods={methods}
          onManage={handleManage}
          onAddNew={handleAddNew}
          gridLayout
          interactive
          onSetAsDefault={handleSetAsDefault}
          onDelete={handleDelete}
          isUpdating={isUpdating}
          showEmptyState
          emptyStateTitle="No saved payment methods"
          emptyStateMessage="Add a credit card or bank account to start making payments."
          style={styles.content}
        />
      </ScrollView>

      <ConfirmationModal
        visible={showDeleteConfirm}
        message="Remove saved payment method?"
        onCancel={cancelDelete}
        onProceed={confirmDelete}
        cancelText="No, cancel"
        proceedText="Proceed"
      />
    </>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    paddingBottom: 24,
  },
  content: {
    padding: 24,
  },
});

export default PaymentMethodsView;
